import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import type { ClaimAssignee, UpdateClaimAccessRequestBlockedModulesEnum } from "@/api/generated/client/models";
import { CLAIM_MODULE_OPTIONS } from "@/constants/claim-options";
import { X } from "lucide-react";

interface AssigneePermissionsEditorProps {
    assignee: ClaimAssignee;
    canWrite: boolean;
    isUpdatingAccess: boolean;
    blockedModules: UpdateClaimAccessRequestBlockedModulesEnum[];
    onAccessLevelChange: (assignee: ClaimAssignee, nextLevel: "ro" | "rw") => Promise<void>;
    onModuleToggle: (
        assignee: ClaimAssignee,
        module: UpdateClaimAccessRequestBlockedModulesEnum,
        checked: boolean,
    ) => Promise<void>;
    showHeader?: boolean;
    onClose?: () => void;
    wrapperClassName?: string;
    moduleGridClassName?: string;
}

export function AssigneePermissionsEditor({
    assignee,
    canWrite,
    isUpdatingAccess,
    blockedModules,
    onAccessLevelChange,
    onModuleToggle,
    showHeader = false,
    onClose,
    wrapperClassName = "rounded-md border bg-background p-2",
    moduleGridClassName = "grid grid-cols-2 gap-x-2 gap-y-1",
}: AssigneePermissionsEditorProps) {
    return (
        <div className={wrapperClassName}>
            {showHeader && (
                <div className="mb-2 flex items-center gap-2">
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Edit permissions</p>
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="ml-auto h-5 w-5"
                            onClick={onClose}
                            aria-label="Close permissions editor"
                            title="Close edit"
                        >
                            <X className="h-3 w-3" />
                        </Button>
                    )}
                </div>
            )}

            <div className="space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Access level</p>
                <Select
                    value={assignee.accessLevel}
                    onValueChange={(value: "ro" | "rw") => void onAccessLevelChange(assignee, value)}
                    disabled={!canWrite || isUpdatingAccess}
                >
                    <SelectTrigger className="h-8 text-xs">
                        <SelectValue placeholder="Access level" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="rw">Can edit</SelectItem>
                        <SelectItem value="ro">Read only</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="mt-2 space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Visible modules</p>
                <div className={moduleGridClassName}>
                    {CLAIM_MODULE_OPTIONS.map((moduleOption) => {
                        const isVisible = !blockedModules.includes(moduleOption.value);
                        return (
                            <label
                                key={moduleOption.value}
                                className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
                            >
                                <Checkbox
                                    checked={isVisible}
                                    onCheckedChange={(checked) =>
                                        void onModuleToggle(assignee, moduleOption.value, checked !== true)
                                    }
                                    disabled={!canWrite || isUpdatingAccess}
                                    className="h-3.5 w-3.5"
                                />
                                <span className="min-w-0 truncate" title={moduleOption.label}>
                                    {moduleOption.label}
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
